import React, { useState, useEffect } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import {
  LayoutDashboard,
  Activity,
  Calendar,
  CalendarDays,
  FileBarChart,
  Settings,
  ChevronLeft,
  ChevronRight,
  MapPin,
  Smartphone,
  Sparkles,
  Key,
  Hammer,
  QrCode,
  ShieldCheck,
  UserCog,
} from 'lucide-react';
import { cn } from '../../lib/utils';
import { useAuthStore } from '../../state/auth';
import { useTranslation } from '../../i18n';

interface NavItem {
  labelKey: string;
  icon: React.ComponentType<{ className?: string }>;
  path: string;
  badge?: number;
}

interface SidebarNavProps {
  isCollapsed?: boolean;
  onToggleCollapse?: () => void;
  onMobileClose?: () => void;
}

// Sidebar order (client-specified). 'nav.schedules' lives beneath Calendar;
// 'nav.alerts' retired onto Compliance; 'nav.patrolReports' lives atop Officers;
// 'nav.admin'/'nav.deploy' live beneath Settings (System Admin).
const getNavItems = (pendingCount: number): NavItem[] => [
  { labelKey: 'nav.dashboard', icon: LayoutDashboard, path: '/supervisor' },
  { labelKey: 'nav.liveActivity', icon: Activity, path: '/supervisor/activity' },
  { labelKey: 'nav.calendar', icon: CalendarDays, path: '/supervisor/calendar' },
  { labelKey: 'nav.officers', icon: UserCog, path: '/supervisor/officers' },
  { labelKey: 'nav.checkpoints', icon: QrCode, path: '/supervisor/checkpoints' },
  { labelKey: 'nav.devices', icon: Smartphone, path: '/supervisor/devices' },
  { labelKey: 'nav.compliance', icon: ShieldCheck, path: '/supervisor/compliance' },
  { labelKey: 'nav.reports', icon: FileBarChart, path: '/supervisor/report-builder' },
  { labelKey: 'nav.askAi', icon: Sparkles, path: '/supervisor/ask' },
  { labelKey: 'nav.settings', icon: Settings, path: '/supervisor/settings', badge: pendingCount > 0 ? pendingCount : undefined },
];

export const SidebarNav: React.FC<SidebarNavProps> = ({
  isCollapsed = false,
  onToggleCollapse,
  onMobileClose,
}) => {
  const location = useLocation();
  const { t } = useTranslation();
  const user = useAuthStore((state) => state.user);
  const [pendingCount, setPendingCount] = useState(0);

  useEffect(() => {
    const updatePendingCount = () => {
      try {
        const storedApplications = JSON.parse(localStorage.getItem("pendingApplications") || "[]");
        const realPendingCount = storedApplications.filter((app: any) => app.status === 'PENDING').length;
        setPendingCount(realPendingCount);
      } catch {
        setPendingCount(0);
      }
    };

    updatePendingCount();

    const handleStorageChange = () => {
      updatePendingCount();
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, []);

  const isAdmin = user?.role === 'ADMIN' || user?.role === 'Administrator';
  const isSupervisor = user?.role === 'SUPERVISOR';

  // Feature restrictions apply ONLY to the SUPERVISOR (view-only) tier. ADMIN
  // "Manager" accounts and DIRECTOR keep full feature access — they are scoped by
  // hospital (data), not by feature. Supervisors lose Ask AI, Admin, Settings and
  // Deploy; they keep the read-only monitoring pages.
  const SUPERVISOR_HIDDEN = new Set([
    '/supervisor/ask',
    '/supervisor/admin',
    '/supervisor/deploy/plan',
  ]);

  const navItems = getNavItems(pendingCount).filter(item => {
    if (isSupervisor) {
      return !SUPERVISOR_HIDDEN.has(item.path);
    }
    // Deploy remains an ADMIN-only tool (its routes are guarded to ADMIN).
    if (item.path === '/supervisor/deploy/plan') {
      return isAdmin;
    }
    return true;
  });

  return (
    // The whole sidebar is the scroll container (not just the item list), so a
    // wheel/trackpad gesture scrolls it no matter where the cursor sits — over
    // the logo, the items, or the footer. Every ancestor of the sidebar is
    // `overflow-hidden`, so a non-scrollable region here would swallow the
    // gesture entirely instead of chaining it anywhere. `sidebar-scroll` keeps
    // the scrollbar permanently visible (macOS hides overlay scrollbars) so the
    // overflow is discoverable rather than something you have to guess at.
    <nav className={cn(
      "flex flex-col h-full max-h-full overflow-y-auto overscroll-contain sidebar-scroll transition-all duration-300",
      "bg-white border-r border-gray-200 shadow-sm",
      isCollapsed ? "w-20" : "w-64"
    )}>
      {/* Sidebar Header with Logo and Collapse Arrow.
          Sticky so the brand and the collapse toggle stay reachable while the
          item list scrolls underneath. bg-inherit picks up the nav's surface in
          both themes (white / navy) without hard-coding either. */}
      <div className="p-6 border-b border-gray-100/50 relative sticky top-0 z-10 bg-inherit shrink-0">
        <div className="flex items-center justify-between mb-2">
          <div className={cn(
            "transition-opacity duration-300",
            isCollapsed ? "opacity-0 w-0 overflow-hidden" : "opacity-100"
          )}>
            {/* In dark mode we invert the logo's colours (no white backing box)
                so the navy Emory lettering reads as light on the dark sidebar.
                In light mode it renders normally. */}
            <div className="inline-flex rounded-md">
              <img
                src="/emory-healthcare-full-logo.png"
                alt="Emory Healthcare"
                className="h-10 w-auto object-contain dark:invert"
              />
            </div>
          </div>
          <button
            onClick={onToggleCollapse}
            className={cn(
              "p-1.5 rounded-lg hover:bg-gray-100 text-gray-400 transition-all shadow-sm border border-gray-50 bg-white",
              isCollapsed ? "mx-auto" : "ml-2"
            )}
            aria-label={isCollapsed ? "Expand" : "Collapse"}
          >
            {isCollapsed ? <ChevronRight className="w-5 h-5" /> : <ChevronLeft className="w-5 h-5" />}
          </button>
        </div>
        {!isCollapsed && (
          <div className="h-0.5 w-8 brand-blue-gradient rounded-sm" />
        )}
      </div>

      {/* Navigation Items.
          Natural height (no flex-1/overflow of its own) — the nav above owns the
          scrolling. The footer's `mt-auto` still pins itself to the bottom when
          the list is short, and simply follows the last item when it isn't. */}
      <div className="py-6 space-y-1">
        {navItems.map((item) => {
          const isActive = location.pathname === item.path ||
            (item.path !== '/supervisor' && location.pathname.startsWith(
              item.path === '/supervisor/deploy/plan' ? '/supervisor/deploy' : item.path
            ));

          return (
            <NavLink
              key={item.path}
              to={item.path}
              onClick={onMobileClose}
              className={cn(
                "flex items-center gap-3 px-4 py-3.5 mx-3 rounded-xl transition-all duration-200",
                isActive
                  ? "brand-header-blue-gradient text-white shadow-md active:scale-95 translate-x-1"
                  : "text-gray-600 hover:bg-blue-50/50 hover:text-blue-700 active:bg-blue-100",
                isCollapsed && "justify-center px-0 mx-2"
              )}
              title={isCollapsed ? t(item.labelKey) : undefined}
            >
              <item.icon className={cn(
                "w-5 h-5 flex-shrink-0 animate-in fade-in zoom-in duration-300",
                isActive ? "text-white" : "text-gray-400 group-hover:text-blue-600"
              )} />

              {!isCollapsed && (
                <span className="font-semibold text-sm tracking-tight">{t(item.labelKey)}</span>
              )}

              {!isCollapsed && item.badge && (
                <span className="ml-auto bg-red-500 text-white text-[10px] font-bold rounded-md px-2 py-0.5">
                  {item.badge}
                </span>
              )}
            </NavLink>
          );
        })}
      </div>

      {/* Sidebar Footer */}
      <div className="mt-auto shrink-0 p-4 border-t border-gray-50">
        <div className={cn(
          "flex items-center gap-3 px-3 py-2 rounded-lg bg-gray-50 text-[10px] text-gray-400 font-bold uppercase tracking-widest",
          isCollapsed && "justify-center"
        )}>
          <MapPin className="w-3.5 h-3.5" />
          {!isCollapsed && <span>{t('brand.guardSystem')}</span>}
        </div>
      </div>
    </nav>
  );
};
